import {
  createFileRoute,
  Outlet,
  Navigate,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useAuthSync } from '@/hooks/use-auth-sync'
import { useMonitoringUser } from '@/hooks/use-monitoring-user'
import { useHttpClientSetup } from '@/hooks/use-http-client-setup'
import { AppSidebar } from '@/components/layout/sidebar/app-sidebar'
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from '@/components/layout/sidebar'
import { Menu } from 'lucide-react'
import { PageContainer } from '@/components/layout/page-container'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import {
  useUserStore,
  selectHasCompassAccess,
  selectRole,
  selectIsProfileReady,
} from '@/stores/user.store'
import logoCompassImg from '@/assets/images/greybull-logo-full.svg'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = createFileRoute('/_authenticated' as any)({
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  const { isSignedIn, isLoaded } = useAuth()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const roleRedirectStarted = useRef(false)
  // The company page and its OGSM screen both manage their own padding.
  const isFullWidthLayout = /^\/companies\/[^/]+(\/ogsm)?\/?$/.test(pathname)

  // Setup HTTP client with Clerk auth
  const isHttpReady = useHttpClientSetup()

  // Sync user with backend and monitoring tools
  const { isLoading: isAuthLoading, error: authError } = useAuthSync()
  useMonitoringUser()

  // Must be called before any early returns (Rules of Hooks)
  const role = useUserStore(selectRole)
  const hasCompassAccess = useUserStore(selectHasCompassAccess)
  const isProfileReady = useUserStore(selectIsProfileReady)
  const shouldRedirectToPortal =
    isProfileReady && Boolean(role?.startsWith('PORTCO_'))

  useEffect(() => {
    if (!shouldRedirectToPortal || roleRedirectStarted.current) return

    roleRedirectStarted.current = true
    void navigate({ to: '/portal', replace: true })
  }, [navigate, shouldRedirectToPortal])

  // Show loading state while Clerk is loading
  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <img src={logoCompassImg} alt="Logo" className="w-auto h-25" />
      </div>
    )
  }

  // Redirect to login if not signed in
  if (!isSignedIn) {
    return <Navigate to="/" />
  }

  // Show loading state while HTTP client or backend profile is loading
  if (!isHttpReady || isAuthLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <img
          src={logoCompassImg}
          alt="Logo"
          className="w-auto h-25 animate-pulse"
        />
      </div>
    )
  }

  if (isProfileReady && !hasCompassAccess) {
    return <Navigate to="/auth/redirect" />
  }

  // Redirect portco users to the portal
  if (shouldRedirectToPortal) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <img
          src={logoCompassImg}
          alt="Logo"
          className="w-auto h-25 animate-pulse"
        />
      </div>
    )
  }

  // Show error state if backend profile failed to load
  if (authError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <img src={logoCompassImg} alt="Logo" className="w-auto h-25" />
        <Typography as="p" className="text-destructive">
          Failed to load user profile
        </Typography>
        <Typography variant="muted">{authError}</Typography>
        <Button
          variant="link"
          size="sm"
          onClick={() => window.location.reload()}
        >
          Try again
        </Button>
      </div>
    )
  }

  return (
    <SidebarProvider>
      <a
        href="#main-content"
        className="fixed left-4 top-4 z-[100] -translate-y-24 rounded-md bg-background px-4 py-2 text-sm font-medium text-primary shadow-md transition-transform focus:translate-y-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Skip to content
      </a>
      <AppSidebar />
      <SidebarInset id="main-content" tabIndex={-1}>
        <div className="flex shrink-0 items-center border-b border-primary/10 px-2 py-1 md:hidden">
          <SidebarTrigger className="w-auto gap-2 px-3">
            <Menu aria-hidden="true" />
            <Typography as="span" variant="body-sm">
              Menu
            </Typography>
          </SidebarTrigger>
        </div>
        <PageContainer
          className={
            isFullWidthLayout
              ? 'gap-0 p-0 md:p-0 2xl:p-0 overflow-visible'
              : undefined
          }
        >
          <Outlet />
        </PageContainer>
      </SidebarInset>
    </SidebarProvider>
  )
}
